import { Permissions, PermitRender } from '@checkit/react-components';
import { CheckpointIcon, CheckpointTypeIcon } from '@checkit/react-components/build/components/Icons';
import { Typography } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import MenuItem from '@material-ui/core/MenuItem';
import { useTheme } from '@material-ui/core/styles';
import DashboardIcon from '@material-ui/icons/Dashboard';
import GroupIcon from '@material-ui/icons/Group';
import LocalLibraryIcon from '@material-ui/icons/LocalLibrary';
import React, { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';
import MaterialUiLink from '../../components/MaterialUiLink';
import { messages } from '../../dashboard/Dashboard.translations';
import { appMessages } from '../../translations';
import styles from './styles';

interface Props {
  handleDrawerToggle: () => void;
  mobileOpen: boolean;
}

export interface ItemListItemProps {
  index: number;
  name: string;
  childIcon?: ReactElement;
  to: string;
  permissions?: Permissions;
}

export const SideNavbar = (props: Props) => {
  const { handleDrawerToggle, mobileOpen } = props;

  const theme = useTheme();
  const classes = styles();

  const SideNavMenuItem = ({ name, childIcon, to, permissions = {} }: ItemListItemProps) => {
    const routeMatch = useRouteMatch(to) || false;

    return (
      <PermitRender
        permissions={permissions}
      >
        <MenuItem
          button={true}
          selected={routeMatch && routeMatch.isExact}
          component={MaterialUiLink}
          to={to}
        >
          <ListItem >
            <ListItemIcon>{childIcon}</ListItemIcon>
            <ListItemText primary={name} />
          </ListItem>
        </MenuItem>
      </PermitRender>
    );
  };

  const checkpointsPermit: Permissions = {
    permittedRoles: ['administrator'],
    requiredFeatures: ['QR-SCANNING']
  };

  const librariesPermit: Permissions = {
    permittedRoles: ['safetyManager'],
    requiredFeatures: ['LIBRARIES']
  };

  const userDefinedRolesPermit: Permissions = {
    permittedRoles: ['administrator'],
    requiredFeatures: ['USER-DEFINED-ROLES']
  };

  const { formatMessage } = useIntl();
  const checkpointListItems = [
    {
      childIcon: <CheckpointTypeIcon />,
      name: formatMessage(messages['dashboard.checkpointType.title']),
      permissions: checkpointsPermit,
      to: '/checkpointtypes'
    },
    {
      childIcon: <CheckpointIcon />,
      name: formatMessage(messages['dashboard.checkpoint.title']),
      permissions: checkpointsPermit,
      to: '/checkpoints'
    }
    // {
    //   childIcon: <DocumentIcon />,
    //   name: 'Documents',
    //   to: '/documents'
    // }
  ];

  const libraryListItems = [
    {
      name: formatMessage(messages['dashboard.libraries.title']),
      permissions: librariesPermit,
      to: '/libraries'
    }
  ];

  const userDefinedRolesListItems = [
    {
      name: formatMessage(messages['dashboard.userRoles']),
      permissions: userDefinedRolesPermit,
      to: '/user-defined-roles'
    }
  ];

  const content = (clickHandler?: typeof handleDrawerToggle) => (
    <div className={classes.sideBarList}>
      <div className={classes.appBar} />
      <Hidden xsDown={true}>
        <Typography variant="h6" align="center">
          {formatMessage(appMessages['app.name'])}
        </Typography>
        <Typography variant="caption" align="center" display="block">
          {formatMessage(appMessages['app.tagLine'])}
        </Typography>
      </Hidden>
      <List component="nav" onClick={clickHandler}>
        <SideNavMenuItem
          index={4}
          name={formatMessage(messages['dashboard.name'])}
          childIcon={<DashboardIcon />}
          to="/"
        />
        <PermitRender
          permissions={checkpointsPermit}
        >
        <Divider />
        <ListSubheader>{formatMessage(messages['dashboard.checkPoints'])}</ListSubheader>
        {checkpointListItems.map((item, index) => (
          <SideNavMenuItem
            key={index}
            index={index}
            name={item.name}
            childIcon={item.childIcon}
            to={item.to}
            permissions={item.permissions}
          />
        ))}
        </PermitRender>
        <PermitRender
          permissions={librariesPermit}
        >
          <Divider />
          <ListSubheader>{formatMessage(messages['dashboard.libraryManagement'])}</ListSubheader>
          {libraryListItems.map((item, index) => (
            <SideNavMenuItem
              key={index}
              index={index}
              name={item.name}
              childIcon={<LocalLibraryIcon />}
              to={item.to}
              permissions={item.permissions}
            />
          ))}
        </PermitRender>
        <PermitRender
          permissions={userDefinedRolesPermit}
        >
          <Divider />
          <ListSubheader>{formatMessage(messages['dashboard.userDefinedRoles'])}</ListSubheader>
          {userDefinedRolesListItems.map((item, index) => (
            <SideNavMenuItem
              key={index}
              index={index}
              name={item.name}
              childIcon={<GroupIcon />}
              to={item.to}
              permissions={item.permissions}
            />
          ))}
        </PermitRender>
      </List>
    </div>
  );

  return (
    <nav className={classes.drawer}>
      <Hidden smUp={true} implementation="css">
        <Drawer
          variant="temporary"
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{ keepMounted: true }}
        >
          {content(handleDrawerToggle)}
        </Drawer>
      </Hidden>
      <Hidden xsDown={true} implementation="css">
        <Drawer
          classes={{ paper: classes.drawerPaper }}
          variant="permanent"
          open={true}
        >
          {content()}
        </Drawer>
      </Hidden>
    </nav>
  );
};
